import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const labImages = [
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/e115cc9f7ced612cdea6772ae65e5647.jpeg',
    title: 'Pruebas de Demulsificación',
    desc: 'Análisis de separación crudo–agua con muestras numeradas y control estadístico de resultados.',
    icon: 'ri-test-tube-line',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/b8417df79b1d6cde241d720bf645acc0.jpeg',
    title: 'Filtros HPHT OFITE',
    desc: 'Equipos de filtración de alta presión y alta temperatura para evaluar fluidos de perforación bajo condiciones reales de pozo.',
    icon: 'ri-settings-3-line',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/fd513315d64f5abacbccfb701ba417bd.jpeg',
    title: 'Horno de Envejecimiento OFI',
    desc: 'Simulación de condiciones de alta temperatura para el envejecimiento controlado de fluidos y evaluación de estabilidad térmica.',
    icon: 'ri-temp-hot-line',
  },
];

const capabilities = [
  { icon: 'ri-flask-line', text: 'Evaluación de fluidos de perforación' },
  { icon: 'ri-drop-line', text: 'Pruebas de demulsificación y separación' },
  { icon: 'ri-fire-line', text: 'Ensayos HPHT alta presión / alta temperatura' },
  { icon: 'ri-bar-chart-line', text: 'Control estadístico de calidad por lote' },
  { icon: 'ri-microscope-line', text: 'Análisis reológico y filtración' },
  { icon: 'ri-shield-check-line', text: 'Certificación de especificaciones API' },
];

export default function Laboratorio() {
  const [visible, setVisible] = useState(false);
  const [activeImg, setActiveImg] = useState<number | null>(null);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="laboratorio" ref={ref} className="py-20 md:py-28 bg-background-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-accent-400 text-sm font-600 tracking-widest uppercase mb-3 block">
            Infraestructura Técnica
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-white leading-tight mb-4">
            Área de <span className="gradient-text">Laboratorio</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-400 text-base max-w-2xl mx-auto">
            Contamos con un laboratorio propio equipado con tecnología de última generación
            para garantizar la calidad, consistencia y rendimiento de cada producto que fabricamos.
          </p>
        </div>

        {/* Main image grid */}
        <div className="grid lg:grid-cols-3 gap-5 mb-14">
          {labImages.map((img, i) => (
            <div
              key={i}
              onClick={() => setActiveImg(i)}
              className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              {/* Image */}
              <div className="relative h-64 md:h-72 overflow-hidden">
                <img
                  src={img.src}
                  alt={img.title}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/30 to-transparent" />

                {/* Zoom icon */}
                <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-white text-sm"></i>
                </div>
              </div>

              {/* Card content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500/20">
                    <i className={`${img.icon} text-primary-400 text-sm`}></i>
                  </div>
                  <h4 className="font-heading font-700 text-white text-base">{img.title}</h4>
                </div>
                <p className="text-foreground-400 text-xs leading-relaxed">{img.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Capabilities + CTA row */}
        <div className={`grid lg:grid-cols-2 gap-10 items-center transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          {/* Capabilities list */}
          <div>
            <h3 className="font-heading font-700 text-2xl text-white mb-6">
              Capacidades Analíticas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {capabilities.map((cap) => (
                <div key={cap.text} className="flex items-start gap-3 p-3 rounded-lg bg-background-900 border border-background-800">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500/15 flex-shrink-0">
                    <i className={`${cap.icon} text-accent-400 text-sm`}></i>
                  </div>
                  <span className="text-foreground-300 text-sm leading-snug">{cap.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats + CTA */}
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: '3', unit: 'Equipos HPHT', icon: 'ri-settings-3-line' },
                { num: '100%', unit: 'Lotes analizados', icon: 'ri-bar-chart-line' },
                { num: 'API', unit: 'Normas aplicadas', icon: 'ri-award-line' },
                { num: '24h', unit: 'Tiempo de respuesta', icon: 'ri-time-line' },
              ].map((s) => (
                <div key={s.unit} className="p-4 rounded-xl bg-background-900 border border-background-800 text-center">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/15 mx-auto mb-2">
                    <i className={`${s.icon} text-primary-400 text-base`}></i>
                  </div>
                  <div className="font-heading font-700 text-2xl text-white">{s.num}</div>
                  <div className="text-foreground-500 text-xs mt-0.5">{s.unit}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/contacto')}
              className="w-full py-3.5 rounded-lg bg-primary-500 hover:bg-primary-400 text-white font-600 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-mail-send-line"></i>
              Solicitar Análisis de Laboratorio
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {activeImg !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActiveImg(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer hover:bg-white/20 transition-colors"
            onClick={() => setActiveImg(null)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <img
            src={labImages[activeImg].src}
            alt={labImages[activeImg].title}
            className="max-w-4xl w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm text-center">
            {labImages[activeImg].title}
          </p>
        </div>
      )}
    </section>
  );
}